import { StorageAuth } from "./../localStorage/StorageAuth";
import { AxiosAuth } from "../axios";
import { CreateAccessObject } from "./useExtendedApiAccess";
import { AppRouteApi } from "../../config/appRoute";
import { PaginatedData } from "../../models/api";
import { useMemo, useEffect, useState } from "react";
import { ApiResponseContainer } from "../../models/api";
import { useAppDispatch } from "../redux/ReduxStore";
import {
  ApiSchemaInvoice,
  ApiSchemaTaskDate,
  ApiSchemaFilteredInvoice,
  ApiSchemaAppointmentInvoiceRecord,
} from "../../models/api/invoice";
import {
  showErrorToast,
  showLoadingToast,
  showSuccessToast,
} from "../../components/toast.component";
import { ActionsInvoiceTable } from "../redux/slices/sliceInvoiceTable";
import { toaster } from "rsuite";
import { DownloadUtils } from "../../utils";
import { IApiDownload } from "./download";

type TypeApiInvoiceCustomParameter = {
  CompanyId?: string;
  PageNumber?: number;
  PageSize?: number;
  StartDate?: string;
  EndDate?: string;
  SearchKey?: string;
  NextPageToken?: null | string;
};

const Invoice = {
  GetAll: AppRouteApi.Invoice.Root(),
  Tasks: AppRouteApi.Invoice.InvoiceTasks(),
  Submit: AppRouteApi.Invoice.Submit(),
};

function useApiInvoiceGetAll(customParameter: TypeApiInvoiceCustomParameter) {
  const dispatch = useAppDispatch();
  const [invoiceData, setInvoiceData] =
    useState<ApiSchemaFilteredInvoice | null>(null);

  const params: TypeApiInvoiceCustomParameter = useMemo(() => {
    const data: TypeApiInvoiceCustomParameter = {};
    data.CompanyId = customParameter?.CompanyId || StorageAuth.CompanyId;
    if (customParameter?.PageNumber) {
      data.PageNumber = customParameter?.PageNumber || 1;
    }
    if (customParameter?.PageSize) {
      data.PageSize = customParameter?.PageSize || 10;
    }
    if (customParameter?.StartDate) {
      data.StartDate = customParameter?.StartDate;
    }
    if (customParameter?.EndDate) {
      data.EndDate = customParameter?.EndDate;
    }
    if (customParameter?.SearchKey) {
      data.SearchKey = customParameter?.SearchKey;
    }
    data.NextPageToken = `${customParameter?.NextPageToken}`;
    return data;
  }, [customParameter]);

  useEffect(() => {
    let isMounted = true;

    const getData = async () => {
      dispatch(ActionsInvoiceTable.setIsLoading(true));
      try {
        const response = await AxiosAuth.get<
          ApiResponseContainer<ApiSchemaFilteredInvoice>
        >(Invoice.GetAll, {
          params,
        });
        if (response.data.isSuccess) {
          isMounted && setInvoiceData(response.data.data!);
          dispatch(
            ActionsInvoiceTable.setNextPageToken(
              response.data.data?.nextPageToken!
            )
          );
        }
      } catch (error) {
        console.error(error);
      }
      dispatch(ActionsInvoiceTable.setIsLoading(false));
    };
    getData();

    return () => {
      isMounted = false;
    };
  }, [dispatch, params]);

  return {
    invoiceData,
    isInvoiceDataFetching: invoiceData === null,
  };
}

const AccessObject = {
  GetAll: CreateAccessObject<
    PaginatedData<ApiSchemaInvoice>,
    typeof useApiInvoiceGetAll
  >(Invoice.GetAll, useApiInvoiceGetAll),
};

async function UpdateInvoice(invoice: {
  invoiceId: string;
  tasksDates: ApiSchemaTaskDate[];
}): Promise<ApiResponseContainer<ApiSchemaInvoice>> {
  try {
    const response = await AxiosAuth.put<
      ApiResponseContainer<ApiSchemaInvoice>
    >(AppRouteApi.Invoice.Root(), invoice);
    if (response.data.isSuccess) {
      const updAction = (
        res?: PaginatedData<ApiSchemaInvoice>
      ): PaginatedData<ApiSchemaInvoice> | undefined => {
        if (res && Array.isArray(res?.list) && response.data.data) {
          return {
            totalCount: res.totalCount + 1,
            list: [...res.list, response.data.data],
          };
        } else if (res) return res;
      };
      AccessObject.GetAll.Mutate(updAction());
    }
    return response.data;
  } catch (e: any) {
    return showErrorToast({ isSuccess: false, ...JSON.parse(e.message).data });
  }
}

async function GetTask(
  invoiceId: string
): Promise<ApiResponseContainer<ApiSchemaTaskDate[]> | undefined> {
  try {
    const response = await AxiosAuth.get<
      ApiResponseContainer<ApiSchemaTaskDate[]>
    >(AppRouteApi.Invoice.InvoiceTasks(invoiceId));
    return response.data;
  } catch (error: any) {
    showErrorToast({ ...JSON.parse(error.message).data });
  }
}

async function GetInvoiceRecordByAppointmentId(
  invoiceId: string
): Promise<
  ApiResponseContainer<ApiSchemaAppointmentInvoiceRecord[]> | undefined
> {
  try {
    const response = await AxiosAuth.get<
      ApiResponseContainer<ApiSchemaAppointmentInvoiceRecord[]>
    >(AppRouteApi.Invoice.AppointmentInvoiceRecord(invoiceId));
    return response.data;
  } catch (error: any) {
    showErrorToast({ ...JSON.parse(error.message).data });
  }
}

async function SubmitInvoice(
  invoiceId: string
): Promise<ApiResponseContainer<ApiSchemaTaskDate[]>> {
  try {
    const response = await AxiosAuth.post<
      ApiResponseContainer<ApiSchemaTaskDate[]>
    >(AppRouteApi.Invoice.Submit(), { invoiceId });
    return response.data;
  } catch (e: any) {
    return showErrorToast({ isSuccess: false, ...JSON.parse(e.message).data });
  }
}

async function SendEmail(
  emailBody: any
): Promise<ApiResponseContainer<boolean>> {
  let toastId = "";
  try {
    toastId = showLoadingToast("Sending Email...", 150000);
    const response = await AxiosAuth.post<ApiResponseContainer<boolean>>(
      AppRouteApi.Invoice.SendMail(),
      emailBody
    );
    showSuccessToast("Email Sent");
    toaster.remove(toastId);
    return response.data;
  } catch (e: any) {
    toaster.remove(toastId);
    setTimeout(() => {
      showErrorToast({ isSuccess: false, ...JSON.parse(e.message).data });
    }, 500);
    return { isSuccess: false };
  }
}

async function DeleteById(
  invoiceId: string
): Promise<ApiResponseContainer<boolean>> {
  try {
    const response = await AxiosAuth.delete<ApiResponseContainer<boolean>>(
      AppRouteApi.Invoice.DeleteById(invoiceId)
    );
    return response.data;
  } catch (e: any) {
    return showErrorToast({ isSuccess: false, ...JSON.parse(e.message).data });
  }
}

export const DownloadExcelFinance = async (
  invoiceId?: string,
  yearMonth?: string,
  isPatientReport?: boolean
) => {
  const customParams = { invoiceId, yearMonth, isPatientReport };
  !isPatientReport && delete customParams.isPatientReport;
  const { data } = await AxiosAuth.get<ApiResponseContainer<IApiDownload>>(
    AppRouteApi.Invoice.ExcelFinance(),
    {
      params: customParams,
    }
  );
  if (data.isSuccess) {
    await DownloadUtils.downloadS3bucketFile(
      data.data?.url!,
      data.data?.fileName!
    );
  }
};

export const DownloadExcelPreview = async (
  invoiceId?: string,
  fileName?: string,
  yearMonth?: string,
  isPatientReport?: boolean
) => {
  const customParams = { invoiceId, yearMonth, isPatientReport };
  !isPatientReport && delete customParams.isPatientReport;
  try {
    const { data: response } = await AxiosAuth.post<
      ApiResponseContainer<IApiDownload>
    >(AppRouteApi.Invoice.FinancePreview(), customParams);
    if (response.isSuccess) {
      const file = await fetch(
        `${process.env.REACT_APP_ROOT_API_MEDIA}/${response.data?.url}`
      );
      return file;
    } else {
      return null;
    }
  } catch (error) {
    console.error(error);
  }
};

export const ApiInvoice = {
  GetAll: AccessObject.GetAll,
  GetTask,
  UpdateInvoice,
  SubmitInvoice,
  SendEmail,
  DeleteById,
  DownloadExcelFinance,
  DownloadExcelPreview,
  GetInvoiceRecordByAppointmentId,
};
